import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { DesktopLayout } from '@/components/desktop/DesktopLayout';
import { useAuth } from '@/contexts/AuthContext';
import { Toast } from '@/components/common/Toast';

// Desktop components following SRP
import { LoginHeader } from '@/components/desktop/auth/LoginHeader';
import { LoginForm } from '@/components/desktop/auth/LoginForm';
import { LoginActions } from '@/components/desktop/auth/LoginActions';

export function DesktopLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState<{ show: boolean; message: string }>({ show: false, message: '' });

  useEffect(() => {
    if (location.state?.message) {
      setToast({ show: true, message: location.state.message });
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    // 인증 로딩이 완료된 후에만 리다이렉트 처리
    if (!authLoading && user) {
      console.log('[DesktopLogin] 사용자 역할 기반 리다이렉트:', user.role);
      
      // 사용자 역할에 따라 다른 홈페이지로 리다이렉트
      let defaultPath = '/';
      if (user.role === 'seller') {
        defaultPath = '/seller/home';
        console.log('[DesktopLogin] 판매자 홈으로 리다이렉트:', defaultPath);
      } else {
        // buyer의 경우 메인 홈 (관리자는 별도 페이지에서 로그인)
        defaultPath = '/';
        console.log('[DesktopLogin] 구매자 홈으로 리다이렉트:', defaultPath);
      }
      
      const from = location.state?.from || defaultPath;
      navigate(from, { replace: true });
    }
  }, [user, authLoading, navigate, location.state]);

  const handleLoginSuccess = () => {
    // LoginForm에서 로그인 성공 시 호출됨
    // 실제 리다이렉트는 useEffect에서 처리
  };

  const handleLoginError = (message: string) => {
    setToast({ show: true, message });
  };

  // 인증 로딩 중이면 로딩 스피너 표시
  if (authLoading) {
    return (
      <DesktopLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="flex flex-col items-center gap-6">
            <div className="w-12 h-12 border-4 border-gray-200 border-t-[#86DB89] rounded-full animate-spin"></div>
            <p className="text-[#666666] text-[20px]">인증 정보 확인 중...</p>
          </div>
        </div>
      </DesktopLayout>
    );
  }

  return (
    <DesktopLayout>
      <div className="min-h-[80vh] flex items-center justify-center py-12">
        <div className="w-full max-w-[480px] bg-white rounded-2xl shadow-lg border border-gray-200 px-12 py-16">
          {/* Header Section */}
          <LoginHeader />
          
          {/* Form Section */}
          <LoginForm 
            onSuccess={handleLoginSuccess}
            onError={handleLoginError}
            isLoading={isLoading}
            onLoadingChange={setIsLoading}
          />
          
          {/* Actions Section */}
          <LoginActions />
        </div>
      </div>

      {/* 토스트 메시지 */}
      {toast.show && (
        <Toast
          message={toast.message}
          isVisible={toast.show}
          onClose={() => setToast({ show: false, message: '' })}
        />
      )}
    </DesktopLayout>
  );
}